<template>
	<div class="action">
		<template v-if="confirmedColumn">
			<div>
				<CheckAnimate></CheckAnimate>
				<div class="loader-text success-text">
					<span class="loader-text--title">Excel or CSV file imported successfully</span>
					<span class="loader-text--sub-title loader-text--dark">You can always check the status in Import Error section on the left.</span>
				</div>
				<el-button type="primary" @click="pushToAllPatients">
					<strong>Go to All Patients</strong>
				</el-button>
			</div>
		</template>
		<template v-else-if="fileUploading">
			<div>
				<LoadSpinner type="spinner-icon" stroke="#2d3e4f" />
				<div class="loader-text">
					<span class="loader-text--title">Importing patients and events</span>
					<span class="loader-text--sub-title">This might take a while…</span>
				</div>
			</div>
		</template>

		<div v-show="!fileUploading && !fileSelected && !confirmedColumn">
			<input ref="file" type="file" style="display:none" accept=".csv,.xls,.xlsx" @change="readFile">
			<el-button type="primary" @click="selectFile" :disabled="!loadedAllProviderEvents">
				<strong>Import CSV or Excel spreadsheet</strong>
			</el-button>
		</div>
		<template v-if="!fileUploading && fileUploadingError">
			<div>
				<Exclamation />
				<div class="loader-text">
					<span class="loader-text--title">{{ uploadErrorMsg }}</span>
					<div class="action-list">
						<el-button type="primary" plain size="small" @click="pushToAddPatient" class="el-btn el-btn-line">
							Cancel
						</el-button>
						<el-button type="primary" size="small" @click="selectFile" class="el-btn">
							Try Again
						</el-button>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
	import Lottie from 'vue-lottie'
	import CheckAnimate from '../icons/CheckAnimate'
	import LoadSpinner from '@/module/component/icon/LoadSpinner'
	import Exclamation from '@/module/component/icon/Exclamation'
	import {
		mapState,
		mapActions
	} from 'vuex'

	const XLSX_SCRIPTS = [
		'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.8.0/jszip.js',
		'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.8.0/xlsx.js'
	]

	export default {
		name: 'patient-import-upload',

		props: {
			confirmedColumn: {
				type: Boolean
			}
		},

		data() {
			return {
				fileSelected: false,
				importing: false
			}
		},

		mounted() {
			this.$nextTick(() => {
				if (this.loadedAllProviderEvents && !this.confirmedColumn) {
					this.$emit('select')
					this.$refs.file.click()
				}
			})
		},

		methods: {
			...mapActions('provider', [
				'uploadPatientRecordsFile'
			]),
			pushToAllPatients() {
				this.$router.push("/patient")
			},
			pushToAddPatient() {
				this.$router.push("/add/patient")
			},
			selectFile() {
				this.$emit('select')
				this.$refs.file.click()
			},
			loadXLSX() {
				return new Promise((resolve, reject) => {
					let loaded = 0

					// Add all excel parsing scripts
					XLSX_SCRIPTS.forEach((src) => {
						let script = document.createElement('script')
						script.setAttribute('src', src)
						script.onload = function () {
							loaded++
							if (loaded === XLSX_SCRIPTS.length) {
								resolve()
							}
						}
						document.head.appendChild(script)
					})
				})
			},
			loadEvents() {
				if (this.profiles)
					return this.loadContent({
						type: 'event',
						id: [].concat.apply([], this.profiles.map((profile) => profile.event)).join(',')
					})
				else
					return Promise.resolve()
			},
			readFile() {
				if (this.$refs.file && this.$refs.file.files.length > 0) {
					const file = this.$refs.file.files[0]
					this.fileSelected = file
					this.uploadPatientRecordsFile(file)
				}
			},
			readExcel(file) {
				var reader = new FileReader()
				reader.onload = (e) => {
					var workbook = XLSX.read(e.target.result, {
						type: 'binary'
					})
					var rows = []

					workbook.SheetNames.forEach((name) => {
						rows.push.apply(rows, XLSX.utils.sheet_to_json(workbook.Sheets[name], {
							header: 1
						}))
					})

					this.$emit('rows', rows)

					// this.patient = parseFile(rows, this.getProvider, this.existingPatients, this.events)
					// this.importing = this.parsing = false
				}
				reader.onerror = function (evt) {
					console.log(evt)
				}

				reader.readAsBinaryString(file)
			},
			readCSV(file) {
				var reader = new FileReader()
				reader.onload = (e) => {
					// console.log(e.target.result)
					var rows = []
					// TODO: split and parse CSV file
					// this.patient = parseFile(rows, this.getProvider, this.existingPatients, this.events)
					this.$emit('rows', rows)
				}
				reader.onerror = function (e) {
					console.log(e)
				}

				reader.readAsText(file, 'utf8')
			}
		},
		watch: {
			fileUploadSuccess() {
				if(this.fileUploadSuccess) {
					this.$emit('rows', this.fileUploadSuccess.row)
				}
			}
		},
		computed: {
			...mapState('provider', [
				'loadedAllProviderEvents',
				'fileUploading',
				'fileUploadingError',
				'fileUploadSuccess'
			]),
			uploadErrorMsg() {
				return (this.fileUploadingError && this.fileUploadingError.code === 'EmptySpreadsheet') ?
					'Spreadsheet has no rows to parse.' :
					'There was an error in importing the file. Please try again.'
			}
		},

		components: {
			LoadSpinner,
			CheckAnimate,
			Exclamation
		}
	}
</script>

<style lang="scss" scoped>
	@import "@/module/add/import/table/style.scss";
	@import "@/module/app/theme/color.scss";
	.action {
		background: unset !important;
		display: grid;
		padding: 0 !important;
		grid-template-rows: calc(100vh - 33px - 49px) auto;
		align-items: center;
		text-align: center;
	}

	.spinner-icon {
		width: 25px;
		height: 25px;
	}

	.loader-text {
		text-align: center;

		&--title {
			margin: 14px 0 7px;
			font-size: 18px;
			font-weight: 600;
			display: block;
			color: #2d3e4f;
		}

		&--sub-title {
		    margin: 7px 33px 24px;
			font-size: 16px;
			font-weight: 500;
			color: #999999;
			display: block;
		}

		&--dark {
			color: #333333;
		}
	}
	.success-text {
		.loader-text--title {
			color: #44b358 !important;
		}
	}
	.action-list {
		margin-top: 17px;
	}
	.el-btn {
		font-size: 16px;
		font-weight: normal;
		margin: 0 4px;
	}
	.el-btn-line {
		background-color: #ffffff !important;
		border: solid 1px #2d3e4f !important;
		color: #2d3e4f !important;
	}
</style>
